<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import format from 'date-fns/format';
import fromUnixTime from 'date-fns/fromUnixTime';
import reportsAPI from 'dashboard/api/reports';
import {
  downloadCsvFile,
  generateFileName,
} from 'dashboard/helper/downloadHelper';
import { getUnixEndOfDay } from 'helpers/DateHelper';
import CompactDateInboxFilters from './components/CompactDateInboxFilters.vue';

const { t } = useI18n();

const STATUS_ACCEPTING = 'accepting';
const STATUS_NOT_ACCEPTING = 'not_accepting';
const STATUS_LOGGED_OUT = 'logged_out';

const isLoading = ref(false);
const errorMessage = ref('');
const generatedAt = ref(null);
const hideInactive = ref(true);

const filters = ref({
  from: 0,
  to: 0,
  inboxId: null,
  userIds: [],
  teamId: null,
});

const forcedFrom = ref(null);
const forcedTo = ref(null);

const summary = ref({
  current_period: {},
});

const agents = ref([]);

const hourTicks = computed(() => Array.from({ length: 25 }, (_, hour) => hour));

const hoveredSegment = ref(null);

const statusLegend = [
  {
    key: STATUS_ACCEPTING,
    className: 'aa-legend-dot--accepting',
    labelKey: 'Active',
    secondsKey: 'accepting_seconds',
  },
  {
    key: STATUS_NOT_ACCEPTING,
    className: 'aa-legend-dot--not-accepting',
    labelKey: 'Inactive',
    secondsKey: 'not_accepting_seconds',
  },
];

const defaultTickLabels = [
  '12 AM',
  '4 AM',
  '8 AM',
  '12 PM',
  '4 PM',
  '8 PM',
  '12 AM',
];

const currentPeriodLabel = computed(() => {
  const period = summary.value.current_period || {};
  if (!period.from || !period.to) return '-';
  return `${format(fromUnixTime(period.from), 'dd MMM yy')} - ${format(
    fromUnixTime(period.to),
    'dd MMM yy'
  )}`;
});

const hasData = computed(() => agents.value.length > 0);

const displayedAgents = computed(() => {
  if (!hideInactive.value) return agents.value;
  return agents.value.filter(agent => Number(agent.active_seconds || 0) > 0);
});

const hourHeaderLabels = computed(() =>
  Array.from({ length: 24 }, (_, hour) => {
    const hour12 = hour % 12 || 12;
    const suffix = hour < 12 ? 'am' : 'pm';
    return `${hour12}:00 ${suffix}`;
  })
);

const timelineTickLabels = computed(() => {
  const period = summary.value.current_period || {};
  if (!period.from || !period.to) return defaultTickLabels;

  const from = fromUnixTime(period.from);
  const to = fromUnixTime(period.to + 1);
  const totalMs = to.getTime() - from.getTime();
  if (totalMs <= 0) return defaultTickLabels;

  const oneDayMs = 24 * 60 * 60 * 1000;
  if (totalMs <= oneDayMs + 1000) {
    return defaultTickLabels;
  }

  return Array.from({ length: 7 }, (_, index) => {
    const timestamp = from.getTime() + (totalMs * index) / 6;
    return format(new Date(timestamp), 'dd MMM');
  });
});

const normalizeStatus = value => {
  if (value === STATUS_ACCEPTING) return STATUS_ACCEPTING;
  return STATUS_NOT_ACCEPTING;
};

const statusText = status => {
  if (status === STATUS_ACCEPTING) {
    return 'Active';
  }
  return 'Inactive';
};

const statusSegmentClass = status => {
  if (status === STATUS_ACCEPTING) return 'aa-segment--accepting';
  return 'aa-segment--not-accepting';
};

const formatDurationBadge = totalSeconds => {
  const seconds = Math.max(Math.round(Number(totalSeconds || 0)), 0);
  if (!seconds) return '-';

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainderSeconds = seconds % 60;

  if (hours > 0) {
    return `${String(hours).padStart(2, '0')}h ${String(minutes).padStart(2, '0')}m`;
  }
  if (minutes > 0) {
    return `${minutes}m ${String(remainderSeconds).padStart(2, '0')}s`;
  }
  return `${remainderSeconds}s`;
};

const formatDurationDetailed = totalSeconds => {
  const seconds = Math.max(Math.round(Number(totalSeconds || 0)), 0);
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  return `${String(hours).padStart(2, '0')}h ${String(minutes).padStart(2, '0')}m`;
};

const toCsvValue = value => {
  const stringValue = String(value ?? '');
  if (/[",\n]/.test(stringValue)) {
    return `"${stringValue.replace(/"/g, '""')}"`;
  }
  return stringValue;
};

const rowsToCsv = (headers, rows) => {
  const csvRows = [headers, ...rows];
  return csvRows
    .map(row => row.map(column => toCsvValue(column)).join(','))
    .join('\n');
};

const fallbackSegmentsFromHours = statuses => {
  const normalized = (statuses || []).map(normalizeStatus);
  if (!normalized.length) return [];

  const periodStart = Number(summary.value.current_period?.from || 0);
  const buckets = [];
  let startHour = 0;
  let current = normalized[0];

  for (let hour = 1; hour <= normalized.length; hour += 1) {
    const status = normalized[hour];
    if (status !== current) {
      const endHour = hour;
      const hoursSpan = endHour - startHour;
      const from = periodStart + startHour * 3600;
      const to = periodStart + endHour * 3600;
      const durationSeconds = Math.max(hoursSpan * 3600, 0);

      buckets.push({
        status: current,
        from,
        to,
        duration_seconds: durationSeconds,
        start_percent: (startHour / 24) * 100,
        width_percent: (hoursSpan / 24) * 100,
      });

      current = status;
      startHour = hour;
    }
  }

  return buckets;
};

const timelineSegments = agent => {
  const rawSegments = (agent.timeline_segments || [])
    .map(segment => ({
      ...segment,
      status: normalizeStatus(segment.status),
      start_percent: Number(segment.start_percent || 0),
      width_percent: Number(segment.width_percent || 0),
      duration_seconds: Number(segment.duration_seconds || 0),
    }))
    .filter(segment => segment.width_percent > 0)
    .sort((a, b) => a.start_percent - b.start_percent);

  if (rawSegments.length) return rawSegments;
  return fallbackSegmentsFromHours(agent.hourly_statuses || []);
};

const segmentTooltip = segment => {
  const startAt = Number(segment.from || 0);
  const endAt = Number(segment.to || 0);
  if (!startAt || !endAt) {
    return `${statusText(segment.status)}: ${formatDurationDetailed(
      segment.duration_seconds
    )}`;
  }

  const fromLabel = format(fromUnixTime(startAt), 'hh:mm aaa').toLowerCase();
  const toLabel = format(fromUnixTime(endAt), 'hh:mm aaa').toLowerCase();

  return `${fromLabel} - ${toLabel} | ${statusText(
    segment.status
  )}: ${formatDurationDetailed(segment.duration_seconds)}`;
};

const segmentTimeRangeLabel = segment => {
  const startAt = Number(segment.from || 0);
  const endAt = Number(segment.to || 0);
  if (!startAt || !endAt) return '';
  const fromLabel = format(fromUnixTime(startAt), 'hh:mm aaa').toLowerCase();
  const toLabel = format(fromUnixTime(endAt), 'hh:mm aaa').toLowerCase();
  return `${fromLabel} - ${toLabel}`;
};

const onSegmentEnter = ({ agentId, segment, index }) => {
  const start = Number(segment.start_percent || 0);
  const width = Number(segment.width_percent || 0);
  const center = start + width / 2;
  hoveredSegment.value = {
    agentId,
    index,
    segment,
    centerPercent: Number.isFinite(center) ? center : 0,
  };
};

const onSegmentLeave = ({ agentId, index }) => {
  if (!hoveredSegment.value) return;
  if (
    hoveredSegment.value.agentId === agentId &&
    hoveredSegment.value.index === index
  ) {
    hoveredSegment.value = null;
  }
};

const onFilterChange = ({
  from,
  to,
  selectedInbox,
  selectedAgents,
  selectedTeam,
}) => {
  const selectedUserIds = Array.isArray(selectedAgents)
    ? selectedAgents.map(agent => Number(agent?.id)).filter(id => id > 0)
    : [];

  filters.value = {
    from,
    to,
    inboxId: selectedInbox?.id,
    userIds: selectedUserIds,
    teamId: selectedTeam?.id || null,
  };
  forcedFrom.value = from;
  forcedTo.value = to;
  fetchReport();
};

const hourStatusLabel = status => statusText(normalizeStatus(status));

const fetchReport = async () => {
  if (!filters.value.from || !filters.value.to) return;

  isLoading.value = true;
  errorMessage.value = '';

  try {
    const response = await reportsAPI.getAgentActivity({
      from: filters.value.from,
      to: filters.value.to,
      inboxId: filters.value.inboxId,
      userIds: filters.value.userIds,
      teamId: filters.value.teamId,
    });

    summary.value = response.data?.summary || summary.value;
    agents.value = response.data?.agents || [];
    generatedAt.value = new Date();
  } catch {
    errorMessage.value = t('AGENT_ACTIVITY_REPORT.FETCH_ERROR');
    agents.value = [];
  } finally {
    isLoading.value = false;
  }
};

const shiftRange = direction => {
  if (!filters.value.from || !filters.value.to) return;
  const spanSeconds = Math.max(
    filters.value.to - filters.value.from + 1,
    86_400
  );
  let nextFrom = filters.value.from + direction * spanSeconds;
  let nextTo = filters.value.to + direction * spanSeconds;

  const todayEnd = getUnixEndOfDay(new Date());
  if (nextTo > todayEnd) {
    const overflow = nextTo - todayEnd;
    nextFrom -= overflow;
    nextTo = todayEnd;
  }

  if (nextFrom < 0 || nextTo <= nextFrom) return;

  forcedFrom.value = nextFrom;
  forcedTo.value = nextTo;
  filters.value = {
    ...filters.value,
    from: nextFrom,
    to: nextTo,
  };
};

const canShiftForward = computed(() => {
  if (!filters.value.to) return false;
  return filters.value.to < getUnixEndOfDay(new Date());
});

const canShiftBackward = computed(() =>
  Boolean(filters.value.from && filters.value.to)
);

const downloadBreakdownCsv = () => {
  const headers = [
    t('AGENT_ACTIVITY_REPORT.COLUMN_AGENT'),
    t('AGENT_ACTIVITY_REPORT.COLUMN_EMAIL'),
    'Active',
    'Inactive',
    ...hourHeaderLabels.value,
  ];

  const rows = displayedAgents.value.map(agent => [
    agent.name,
    agent.email,
    formatDurationBadge(agent.accepting_seconds),
    formatDurationBadge(agent.not_accepting_seconds),
    ...(agent.hourly_statuses || []).map(status => hourStatusLabel(status)),
  ]);

  const csvContent = rowsToCsv(headers, rows);
  const fileName = generateFileName({
    type: 'agent-activity-breakdown',
    to: filters.value.to,
  });
  downloadCsvFile(fileName, csvContent);
};

const generatedAtLabel = computed(() =>
  generatedAt.value
    ? generatedAt.value.toLocaleString()
    : t('LAST_7_DAYS_REPORT.NOT_GENERATED_YET')
);
</script>

<template>
  <div class="aa-page">
    <div class="aa-shell">
      <div class="aa-header">
        <div class="aa-header-left">
          <h1 class="aa-title">{{ t('AGENT_ACTIVITY_REPORT.TITLE') }}</h1>
        </div>
      </div>

      <div class="aa-toolbar">
        <CompactDateInboxFilters
          :show-add-filter="false"
          show-agents
          show-teams
          :forced-from="forcedFrom"
          :forced-to="forcedTo"
          @filter-change="onFilterChange"
        />
      </div>

      <div class="aa-content">
        <div v-if="errorMessage" class="aa-error">
          {{ errorMessage }}
        </div>

        <section class="aa-card">
          <div class="aa-card-header">
            <div class="aa-card-title-wrap">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                width="20"
                height="20"
              >
                <path
                  stroke="currentColor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 8h.01M11 12h1v4h1M3 12a9 9 0 1 0 18.001 0A9 9 0 0 0 3 12"
                />
              </svg>
              <h2 class="aa-card-title">
                {{ t('AGENT_ACTIVITY_REPORT.TITLE') }}
              </h2>
              <span class="aa-period">{{ currentPeriodLabel }}</span>
            </div>
            <button
              class="aa-export-btn"
              type="button"
              @click="downloadBreakdownCsv"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                width="16"
                height="16"
              >
                <path
                  stroke="currentcolor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2m-8-1V4m0 12 5-5m-5 5-5-5"
                />
              </svg>
              <span>{{ t('AGENT_ACTIVITY_REPORT.EXPORT_CSV') }}</span>
            </button>
          </div>

          <div class="aa-timeline-header">
            <label class="aa-switch">
              <span>{{ t('AGENT_ACTIVITY_REPORT.HIDE_INACTIVE') }}</span>
              <span class="aa-switch-track" :class="{ 'is-on': hideInactive }">
                <input
                  v-model="hideInactive"
                  type="checkbox"
                  class="aa-switch-input"
                />
                <span class="aa-switch-thumb" />
              </span>
            </label>
            <div class="aa-ticks-nav">
              <button
                class="aa-ticks-btn"
                type="button"
                aria-label="Previous"
                :disabled="!canShiftBackward"
                @click="shiftRange(-1)"
              >
                <span class="aa-ticks-btn-icon" aria-hidden="true">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    width="16"
                    height="16"
                  >
                    <path
                      stroke="currentcolor"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="m6 9 6 6 6-6"
                    />
                  </svg>
                </span>
              </button>
              <div class="aa-ticks">
                <span
                  v-for="(tick, index) in timelineTickLabels"
                  :key="`${tick}-${index}`"
                >
                  {{ tick }}
                </span>
              </div>
              <button
                class="aa-ticks-btn"
                type="button"
                aria-label="Next"
                :disabled="!canShiftForward"
                @click="shiftRange(1)"
              >
                <span
                  class="aa-ticks-btn-icon aa-ticks-btn-icon--next"
                  aria-hidden="true"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    width="16"
                    height="16"
                  >
                    <path
                      stroke="currentcolor"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="m6 9 6 6 6-6"
                    />
                  </svg>
                </span>
              </button>
            </div>
          </div>

          <div class="aa-list">
            <woot-loading-state
              v-if="isLoading"
              class="aa-loading"
              :message="$t('REPORT.LOADING_CHART')"
            />

            <div
              v-else-if="!hasData || !displayedAgents.length"
              class="aa-empty"
            >
              {{ t('AGENT_ACTIVITY_REPORT.NO_DATA') }}
            </div>

            <div v-else>
              <div
                v-for="agent in displayedAgents"
                :key="agent.user_id"
                class="aa-row"
              >
                <div class="aa-row-agent">
                  <img
                    v-if="agent.avatar_url"
                    :src="agent.avatar_url"
                    :alt="agent.name"
                    class="aa-avatar"
                  />
                  <div v-else class="aa-avatar aa-avatar--fallback">
                    {{ (agent.name || '?').charAt(0).toUpperCase() }}
                  </div>
                  <div class="aa-agent-info">
                    <span class="aa-agent-name">{{ agent.name }}</span>
                    <span class="aa-agent-email">{{ agent.email }}</span>
                  </div>
                </div>

                <div class="aa-row-activity">
                  <div class="aa-activity-chart">
                    <div class="aa-hour-ticks" aria-hidden="true">
                      <span
                        v-for="hour in hourTicks"
                        :key="`tick-${agent.user_id}-${hour}`"
                        class="aa-hour-tick"
                        :class="{ 'is-major': hour % 4 === 0 }"
                      />
                    </div>

                    <div class="aa-track">
                      <div class="aa-track-inner">
                        <div
                          v-for="(segment, index) in timelineSegments(agent)"
                          :key="`${agent.user_id}-${index}`"
                          class="aa-segment"
                          :class="statusSegmentClass(segment.status)"
                          :style="{
                            left: `${segment.start_percent}%`,
                            width: `${segment.width_percent}%`,
                          }"
                          :title="segmentTooltip(segment)"
                          @mouseenter="
                            onSegmentEnter({
                              agentId: agent.user_id,
                              segment,
                              index,
                            })
                          "
                          @mouseleave="
                            onSegmentLeave({ agentId: agent.user_id, index })
                          "
                        />
                      </div>

                      <div
                        v-if="hoveredSegment?.agentId === agent.user_id"
                        class="aa-segment-tooltip"
                        :style="{ left: `${hoveredSegment.centerPercent}%` }"
                      >
                        <div class="aa-segment-tooltip__time">
                          {{ segmentTimeRangeLabel(hoveredSegment.segment) }}
                        </div>
                        <div class="aa-segment-tooltip__line">
                          {{ statusText(hoveredSegment.segment.status) }}:
                          <strong>{{
                            formatDurationDetailed(
                              hoveredSegment.segment.duration_seconds
                            )
                          }}</strong>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div class="aa-legend-row">
                    <span
                      v-for="legend in statusLegend"
                      :key="`${agent.user_id}-${legend.key}`"
                      class="aa-legend-item"
                    >
                      <span class="aa-legend-dot" :class="legend.className" />
                      <span>{{ legend.labelKey }}:</span>
                      <strong>{{
                        formatDurationBadge(agent[legend.secondsKey])
                      }}</strong>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <p class="aa-footer-note">
          {{
            t('AGENT_ACTIVITY_REPORT.GENERATED_AT', { time: generatedAtLabel })
          }}<br />
          {{ t('LAST_7_DAYS_REPORT.LOCAL_TIME_NOTE') }}
        </p>
      </div>
    </div>
  </div>
</template>

<style scoped>
.aa-page {
  min-height: 100vh;
  min-height: 100dvh;
  width: 100%;
  box-sizing: border-box;
  background: #ffffff;
  color: #111827;
}

.aa-shell {
  min-width: 650px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #ffffff;
  overflow: hidden;
}

.aa-header {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  min-height: 58px;
  padding: 0 24px;
  border-bottom: 1px solid #eceff3;
}

.aa-header-left {
  display: flex;
  align-items: center;
  gap: 0;
}

.aa-title {
  margin: 0;
  font-size: 18px !important;
  line-height: 24px;
  font-weight: 600;
  letter-spacing: -0.02em;
  color: #111827;
}

.aa-toolbar {
  padding: 12px 24px 8px;
  background: #ffffff;
  box-shadow: rgba(0, 0, 0, 0.3) 0 3px 10px -6px;
}

.aa-content {
  padding: 16px 32px;
}

.aa-error {
  margin-bottom: 12px;
  border: 1px solid #fecaca;
  background: #fef2f2;
  color: #b91c1c;
  border-radius: 8px;
  padding: 8px 10px;
  font-size: 13px;
}

.aa-card {
  border-radius: 4px;
  border: 1px solid #d8dee5;
  background: #ffffff;
  box-sizing: border-box;
  margin-bottom: 20px;
  overflow: hidden;
}

.aa-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 18px 20px;
  border-bottom: 1px solid #e5e7eb;
}

.aa-card-title-wrap {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.aa-card-title {
  margin: 0;
  color: #111827;
  font-size: 20px;
  font-weight: 600;
  line-height: 24px;
}

.aa-period {
  margin-left: 8px;
  font-size: 12px;
  line-height: 18px;
  color: #6b7280;
  white-space: nowrap;
}

.aa-export-btn {
  min-width: 128px;
  height: 36px;
  border-radius: 8px;
  border: 1px solid #d1d5db;
  background: #ffffff;
  color: #111827;
  padding: 0 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
}

.aa-export-btn:hover {
  background: #f9fafb;
}

.aa-timeline-header {
  position: sticky;
  top: 0;
  z-index: 2;
  display: flex;
  align-items: center;
  border-bottom: 1px solid #e5e7eb;
  box-shadow:
    0 1px 4px 0 rgba(221, 226, 230, 0.6),
    inset 0 -1px 0 0 #e5e7eb,
    inset 0 1px 0 0 #e5e7eb;
  background: #ffffff;
  padding: 8px 0;
}

.aa-switch {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 260px;
  padding: 0 20px;
  color: #6b7280;
  font-size: 12px;
  line-height: 16px;
  text-transform: uppercase;
}

.aa-switch-input {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
}

.aa-switch-track {
  position: relative;
  width: 27px;
  height: 20px;
  border-radius: 15px;
  background: #d1d5db;
  cursor: pointer;
  transition: background 0.2s ease;
}

.aa-switch-track.is-on {
  background: rgb(75, 182, 120);
}

.aa-switch-thumb {
  position: absolute;
  top: 2px;
  left: 2px;
  width: 15px;
  height: 15px;
  border-radius: 50%;
  background: #ffffff;
  box-shadow:
    rgba(0, 0, 0, 0.05) 0 3px 3px 0,
    rgba(0, 0, 0, 0.1) 0 2px 2px 0,
    rgba(0, 0, 0, 0.05) 0 3px 2px 0;
  transition: left 0.2s ease-in-out;
}

.aa-switch-track.is-on .aa-switch-thumb {
  left: 10px;
}

.aa-ticks {
  flex: 1 1 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 8px;
  color: #131317;
  font-size: 12px;
  font-weight: 600;
}

.aa-ticks-nav {
  flex: 1 1 auto;
  display: flex;
  align-items: center;
  height: 35px;
  padding: 0 8px;
  gap: 8px;
}

.aa-ticks-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: none;
  background: #ffffff;
  color: #111827;
  cursor: pointer;
  box-shadow: 0 1px 2px rgba(17, 24, 39, 0.12);
}

.aa-ticks-btn:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.aa-ticks-btn-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transform: rotate(90deg);
}

.aa-ticks-btn-icon--next {
  transform: rotate(-90deg);
}

.aa-list {
  border-top: 1px solid #e5e7eb;
}

.aa-loading,
.aa-empty {
  padding: 80px 16px;
  text-align: center;
  color: #6b7280;
  font-size: 13px;
}

.aa-row {
  display: grid;
  grid-template-columns: minmax(260px, 30%) minmax(420px, 1fr);
  align-items: center;
  border-bottom: 1px solid #e2e2e4;
}

.aa-row-agent {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  min-width: 0;
}

.aa-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
}

.aa-avatar--fallback {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #dbeafe;
  color: #1d4ed8;
  font-size: 14px;
  font-weight: 700;
}

.aa-agent-info {
  display: flex;
  flex-direction: column;
  min-width: 0;
  gap: 2px;
}

.aa-agent-name {
  display: inline-block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 13px;
  line-height: 20px;
  font-weight: 500;
  color: #131317;
}

.aa-agent-email {
  display: inline-block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 12px;
  line-height: 16px;
  color: #62626d;
}

.aa-row-activity {
  padding: 0px 28px 8px 12px;
}

.aa-activity-chart {
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 40px;
  margin-left: auto;
  margin-right: auto;
}

.aa-hour-ticks {
  position: relative;
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  flex: 1 1 auto;
}

.aa-hour-tick {
  width: 1px;
  height: 4px;
  margin-bottom: 7px;
  background-color: #c9c9cd;
  flex: 0 0 auto;
}

.aa-hour-tick.is-major {
  height: 8px;
  background-color: #c9c9cd;
}

.aa-track {
  position: relative;
  width: 100%;
  height: 6px;
  background: #ca0005;
  border-radius: 999px;
  overflow: visible;
}

.aa-track-inner {
  position: absolute;
  inset: 0;
  border-radius: inherit;
  overflow: hidden;
}

.aa-segment {
  position: absolute;
  top: 0;
  height: 6px;
  min-width: 1px;
}

.aa-segment--accepting {
  background: #06b6d4;
}

.aa-segment--not-accepting {
  background: #ca0005;
}

.aa-segment--logged-out {
  background: #ca0005;
  box-shadow: none;
}

.aa-legend-row {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 6px 12px;
  margin-top: 10px;
}

.aa-legend-item {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: #62626d;
  font-size: 9px;
  line-height: 16px;
  min-width: 150px;
}

.aa-legend-item strong {
  color: #131317;
  font-weight: 600;
}

.aa-legend-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  display: inline-block;
}

.aa-legend-dot--accepting {
  background: #06b6d4;
}

.aa-legend-dot--not-accepting {
  background: #ca0005;
}

.aa-legend-dot--logged-out {
  background: #ca0005;
  box-shadow: none;
}

.aa-segment-tooltip {
  position: absolute;
  display: none;
  z-index: 5;
  bottom: 18px;
  transform: translateX(-50%);
  min-width: 230px;
  box-shadow: rgba(0, 0, 0, 0.2) 0 5px 10px 0;
  border-radius: 4px;
  overflow: hidden;
}

.aa-segment-tooltip__time {
  padding: 7px 0 8px;
  font-size: 13px;
  background-color: #f3f4f6;
  color: #111827;
  text-align: center;
}

.aa-segment-tooltip__line {
  padding: 10px 14px 12px;
  background: #ffffff;
  color: #111827;
  text-align: center;
  font-size: 13px;
}

.aa-track:hover .aa-segment-tooltip {
  display: block;
}

.aa-footer-note {
  margin: 12px 0 0;
  color: #6b7280;
  font-size: 12px;
  line-height: 20px;
}

::global(body.dark) .aa-page,
::global(body.dark) .aa-shell,
::global(body.dark) .aa-card,
::global(body.dark) .aa-header,
::global(body.dark) .aa-toolbar,
::global(body.dark) .aa-timeline-header,
::global(body.dark) .aa-row,
::global(body.dark) .aa-row-agent,
::global(body.dark) .aa-row-activity,
::global(body.theme-mix) .aa-page,
::global(body.theme-mix) .aa-shell,
::global(body.theme-mix) .aa-card,
::global(body.theme-mix) .aa-header,
::global(body.theme-mix) .aa-toolbar,
::global(body.theme-mix) .aa-timeline-header,
::global(body.theme-mix) .aa-row,
::global(body.theme-mix) .aa-row-agent,
::global(body.theme-mix) .aa-row-activity {
  background: #ffffff !important;
  border-color: #e5e7eb !important;
  color: #111827 !important;
}

::global(body.dark) .aa-agent-name,
::global(body.dark) .aa-agent-email,
::global(body.dark) .aa-card-value,
::global(body.dark) .aa-card-label,
::global(body.dark) .aa-legend-item,
::global(body.dark) .aa-legend-item strong,
::global(body.theme-mix) .aa-agent-name,
::global(body.theme-mix) .aa-agent-email,
::global(body.theme-mix) .aa-card-value,
::global(body.theme-mix) .aa-card-label,
::global(body.theme-mix) .aa-legend-item,
::global(body.theme-mix) .aa-legend-item strong {
  color: #111827 !important;
}

::global(body.dark) .aa-segment-tooltip,
::global(body.theme-mix) .aa-segment-tooltip {
  background: #ffffff !important;
  border-color: #e5e7eb !important;
  color: #111827 !important;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06) !important;
}

::global(body.dark) .aa-legend-dot--logged-out,
::global(body.theme-mix) .aa-legend-dot--logged-out {
  background: #e5e7eb !important;
}

@media (max-width: 1100px) {
  .aa-content {
    padding: 12px 16px 16px;
  }

  .aa-toolbar {
    padding: 10px 16px 8px;
  }

  .aa-row {
    grid-template-columns: 1fr;
  }

  .aa-row-agent {
    padding-bottom: 8px;
  }

  .aa-row-activity {
    padding: 0 16px 12px;
  }

  .aa-timeline-header {
    flex-direction: column;
    align-items: stretch;
    gap: 8px;
    padding: 10px 0;
  }

  .aa-switch {
    min-width: 0;
    justify-content: flex-start;
  }
}
</style>
